import { Row, Col, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FormField,
  FormPhoneNumber,
} from '../../../components/form/FormBasic';
import { FormFieldTypeEnum } from '../../../enums/FormFieldTypeEnum';

export const CitizenRegisterForm = () => {
  const { t } = useTranslation(['register', 'form', 'common']);

  const formatNationalId = (value: string) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 13);
    const p1 = digits.slice(0, 4);
    const p2 = digits.slice(4, 8);
    const p3 = digits.slice(8, 13);
    return [p1, p2, p3].filter(Boolean).join('-');
  };

  const validateName = (label: string) => (rules: any, value: any) => {
    const field = t(label);

    const val = (value ?? '').toString().trim();
    if (!val) {
      return Promise.reject(`${t('required', {field: field})}`);
    }

    const startsWithLetter = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(val);
    if (!startsWithLetter) {
      return Promise.reject(`${t('invalidInput')}`);
    }

    const allowedCharCount = (val.match(/[A-Za-zÀ-ÖØ-öø-ÿ'-]/g) || []).length;
    if (allowedCharCount < 2) {
      return Promise.reject(`${field} ${t('minCharMessage')}`);
    }

    if (allowedCharCount > 80) {
      return Promise.reject(`${field} ${t('maxCharMessage')}`)
    }

    const validInput = /^(?!.*[-']{2})[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' -]*$/.test(val);
    if (!validInput) {
      return Promise.reject(t('invalidInput'));
    }

    const endsWithLetter = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]$/.test(val);
    if (!endsWithLetter) {
      return Promise.reject(`${t('invalidInput')}`);
    }

    return Promise.resolve();
  };

  const validateEmail = (_: any, value: any) => {
    if (!value) {
      return Promise.reject(t('emailRequired'));
    }
    
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return Promise.reject(t('invalidInput'));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="max-w-screen mx-auto mt-8">
      <p className="mb-6 text-xl font-bold text-primary">
        {t('register:personalInfo', { ns: 'form' })}
      </p>
      <Row gutter={24} className="flex flex-col justify-between md:flex-row">
        <Col xs={24} md={11}>
          <div className="mb-6">
            <FormField
              name="firstName"
              label={t('firstName')}
              placeholder={t('firstNamePlaceHolder')}
              isRequired
              type={FormFieldTypeEnum.INPUT}
              rules={[{ validator: validateName('firstName') }]}
            />
          </div>
          <div className="mb-6">
              <FormField
                name="email"
                label={t('email')}
                placeholder={t('enterEmail')}
                type={FormFieldTypeEnum.EMAIL}
                isRequired
                rules={[
                  { required: true, message: t('emailRequired') },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('emailSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

                      if (!emailPattern.test(value)) {
                        return Promise.reject(t('invalidInput'));
                      }

                      if (value.includes('..')) {
                        return Promise.reject(t('invalidInput'));
                      }

                      if (/^[._-]|[._-]@/.test(value)) {
                        return Promise.reject(t('invalidInput'));
                      }

                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidEmail'));
                    },
                  },
                  
                ]}
              />
            </div>
          
                 
          
          <div className="mb-6">
            <Form.Item
              name="nationalID"
              label={t('nationalId')}
              getValueFromEvent={(e) => formatNationalId(e.target.value)}
              getValueProps={(value) => ({ value: formatNationalId(value as string) })}
              validateTrigger={['onChange', 'onBlur']}
              rules={[
                { required: true, message: t('idRequired') },
                {
                  pattern: /^\d{4}-\d{4}-\d{5}$/,
                  message: t('invalidNationalId'),
                },
              ]}
            >
              <Input placeholder={t('enterNationalId')} maxLength={15} />
            </Form.Item>
          </div>    
         
        </Col>

        <Col xs={24} md={11}>
          <div className="mb-6">
            <FormField
              name="lastName"
              label={t('lastName')}
              placeholder={t('lastNamePlaceHolder')}
              isRequired
              type={FormFieldTypeEnum.INPUT}
              rules={[{ validator: validateName('lastName') }]}
            />
          </div>
          <FormPhoneNumber name="contactNumber" label={t('contactNumber')} isRequired />
        </Col>
      </Row>
    </div>
  );
};
